import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { telegramConversationApi } from "./axios";
import {
  ServiceResponseI,
  TelegramConversationClientI,
  TelegramConversationHistoryI,
} from "../../interfaces";
import { ValidateCurrentToken, LogoutSystem } from "../../helpers";
import { AxiosError } from "axios";

const handleErr = (err: unknown): never => {
  const obj = (err as AxiosError).response;
  if (obj?.status === 401) LogoutSystem();
  throw new Error((obj?.data as Error)?.message ?? "Error");
};

const getTelegramConversationClients = async (): Promise<
  ServiceResponseI<TelegramConversationClientI[]>
> => {
  try {
    const token = ValidateCurrentToken();
    if (!token) throw new Error("TOKEN INVALIDO");
    const { data } = await telegramConversationApi.get<
      ServiceResponseI<TelegramConversationClientI[]>
    >("/clients", {
      headers: { Authorization: token },
    });
    return data;
  } catch (err) {
    return handleErr(err);
  }
};

const getTelegramConversationHistory = async (
  clientId: number,
  page: number,
  limit: number,
): Promise<ServiceResponseI<TelegramConversationHistoryI>> => {
  try {
    const token = ValidateCurrentToken();
    if (!token) throw new Error("TOKEN INVALIDO");
    const { data } = await telegramConversationApi.get<
      ServiceResponseI<TelegramConversationHistoryI>
    >(`/${clientId}`, {
      params: { page, limit },
      headers: { Authorization: token },
    });
    return data;
  } catch (err) {
    return handleErr(err);
  }
};

const deleteTelegramConversation = async (clientId: number) => {
  try {
    const token = ValidateCurrentToken();
    if (!token) throw new Error("TOKEN INVALIDO");
    const { data } = await telegramConversationApi.delete(`/${clientId}`, {
      headers: { Authorization: token },
    });
    return data;
  } catch (err) {
    return handleErr(err);
  }
};

export const useTelegramConversationClients = () => {
  const queryClient = useQueryClient();

  const clientsQuery = useQuery(["telegram-conversation-clients"], () =>
    getTelegramConversationClients(),
  );

  const deleteMutation = useMutation(
    (clientId: number) => deleteTelegramConversation(clientId),
    {
      onSuccess: () =>
        queryClient.invalidateQueries(["telegram-conversation-clients"]),
    },
  );

  return { clientsQuery, deleteMutation };
};

export const useTelegramConversationHistory = (
  clientId: number | null,
  page: number,
  limit: number,
) => {
  const historyQuery = useQuery(
    ["telegram-conversation-history", clientId, page, limit],
    () => getTelegramConversationHistory(clientId as number, page, limit),
    { enabled: !!clientId },
  );

  return { historyQuery };
};
